import PropTypes from 'prop-types';
import { useRef, useEffect, useLayoutEffect, useMemo, useCallback, useState, forwardRef } from 'react';
import { useModal } from '../../../../../context/ModalContext';
import TimelinePagination from '../../../../TimelinePagination';
import useScrollPagination from '../../../../../hooks/useScrollPagination';
import MessageInput from './MessageInput';
import UnreadMessageMarker from './UnreadMessageMarker';
import DateSeparator from './DateSeparator';
import SystemMessageSeparator from './SystemMessageSeparator';
import { useMessageDraftsContext } from '../../../../../context/MessageDraftsContext';
import { TIMELINE_SYSTEM_EVENT_TYPES } from '../../../../../constants';
import { useReadMarker } from '../../../../../hooks/useReadMarker';
import logger from '../../../../../utils/logger';
import MessageGroup from './MessageGroup';
import './Messages.css';

const Messages = forwardRef(({
  client,
  roomId,
  messages = [],
  typingText,
  disabled = false,
  placeholder = 'Type a message...',
  onSendMessage = () => {},
  onDeleteMessage = null, // Optional: only for existing chats
  onTriggerFileSelect = () => {},
  // Props for NewChatView (when context is not available)
  value: propValue,
  onSetMessage: propOnSetMessage,
  // Timeline pagination props
  isLoadingOlder = false,
  isLoadingNewer = false,
  canPaginateBackward = false,
  canPaginateForward = false,
  windowPosition = 'live',
  isFillingViewport = false,
  onPaginateBackward = () => {},
  onPaginateForward = () => {},
  onReactionDetailsOpen = () => {},
  onMessageDetailsOpen = () => {},
  onFocusMessage = () => {},
  onContactClick = () => {},
  selectedMessageId = null,
  readMarkerId = null,
  setReadMarker = () => {},
  onEditHistoryOpen = () => {},
  unreadCount = 0,
}, ref) => {
  const { showError } = useModal();
  // Consume the context to get all draft-related state and functions
  // Context may be null when used in NewChatView (no roomId)
  const draftsContext = useMessageDraftsContext();
  const {
    replyingTo = null,
    startReply = () => {},
    cancelReply = () => {},
    getReplyContent = () => ({}),
    editingMessage = null,
    startEdit = () => {},
    cancelEdit = () => {},
    getEditContent = () => ({}),
    isRestoringRef = { current: false },
    currentMessage: contextValue = '',
    onSetMessage: contextOnSetMessage = () => {},
  } = draftsContext || {};
  
  // Use prop values if context is not available (NewChatView scenario)
  // When context exists, use context values; otherwise fallback to props
  const value = draftsContext ? contextValue : (propValue || '');
  const onSetMessage = useMemo(
    () => draftsContext ? contextOnSetMessage : (propOnSetMessage || (() => {})),
    [draftsContext, contextOnSetMessage, propOnSetMessage],
  );
  
  const messagesListRef = useRef(null);
  const messageInputRef = useRef(null);
  const previousScrollHeightRef = useRef(0);
  const [scrollContainer, setScrollContainer] = useState(null);
  const [showReadMarker, setShowReadMarker] = useState(false);

  // Callback ref to handle both forwarded ref and internal ref
  const setMessagesListRef = useCallback((node) => {
    messagesListRef.current = node;

    // Forward the ref to parent if provided
    if (ref) {
      if (typeof ref === 'function') {
        ref(node);
      } else {
        ref.current = node;
      }
    }
  }, [ref]);

  const { updateReadMarkerOnSend } = useReadMarker({
    scrollContainer,
    roomId,
    messages,
    readMarkerId,
    setReadMarker,
    client,
  });

  useEffect(() => {
    if (messagesListRef.current && scrollContainer !== messagesListRef.current) {
      setScrollContainer(messagesListRef.current);
    }
  }, [scrollContainer, roomId]);

  // Set up scroll pagination
  const { maintainScrollPosition, autoScrollToNewMessages } = useScrollPagination({
    scrollContainer,
    canPaginateBackward,
    canPaginateForward,
    isLoadingOlder,
    isLoadingNewer,
    isFillingViewport,
    onPaginateBackward,
    onPaginateForward,
  });

  useEffect(() => {
    if (isLoadingOlder || isFillingViewport || windowPosition !== 'live') {
      return;
    }
    
    if (messagesListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;
      
      // Only auto-scroll if user is already near the bottom.
      if (scrollBottom <= 200) {
        messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
      }
    }
  }, [messages, windowPosition, isLoadingOlder, isFillingViewport]);

  // Focus input when reply is started
  useEffect(() => {
    if (replyingTo && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [replyingTo]);

  // Handle scroll position when new messages are loaded at the top
  useEffect(() => {
    // Only maintain scroll position when we have a previous height reference
    // and we're not at the very beginning
    if (messagesListRef.current && previousScrollHeightRef.current > 0) {
      maintainScrollPosition(previousScrollHeightRef.current);
    }
    
    // Always update the previous scroll height for next time
    if (messagesListRef.current) {
      previousScrollHeightRef.current = messagesListRef.current.scrollHeight;
    }
  }, [messages, maintainScrollPosition, isLoadingOlder, windowPosition]);

  // Auto-scroll when new live messages arrive (only if user is near bottom)
  useEffect(() => {
    // Only auto-scroll for new live messages, not when loading older messages
    if (windowPosition === 'live' && messages.length > 0 && !isLoadingOlder && !isFillingViewport) {
      autoScrollToNewMessages();
    }
  }, [messages.length, windowPosition, autoScrollToNewMessages, isLoadingOlder, isFillingViewport]);

  // Scroll to the correct position when the room changes.
  useLayoutEffect(() => {
    // This effect should only run on initial load of a room's messages.
    if (messages.length === 0 || windowPosition !== 'live' || !messagesListRef.current) {
      return;
    }

    // By using useLayoutEffect, we can scroll before the browser paints, preventing a flash.
    // No timeout is needed.
    const unreadMarker = messagesListRef.current.querySelector('#unread-marker');
    if (unreadMarker) {
      unreadMarker.scrollIntoView({ behavior: 'auto', block: 'center' });
      logger.debug('Scrolled to unread marker on room load.');
    } else {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  }, [roomId, messages.length, readMarkerId]);

  // Focus input and set/clear text when edit state changes.
  useEffect(() => {
    if (editingMessage && messageInputRef.current) {
      messageInputRef.current.focus();
      if (!isRestoringRef.current) {
        onSetMessage(editingMessage.originalContent);
      }
    } else if (!editingMessage && !isRestoringRef.current) {
      onSetMessage('');
    }
  }, [editingMessage, isRestoringRef, onSetMessage]);

  // Initialize and reset the read marker visibility when the room changes.
  // Show marker if there are any unreads when the room loads.
  // Note: This should only run when roomId changes, NOT when unreadCount changes,
  // to preserve the "sticky" behavior where the marker stays visible even after unreads go to 0.
  useEffect(() => {
    setShowReadMarker(unreadCount > 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]); // Only depend on roomId, capture unreadCount at mount time

  const handleDeleteMessage = async (messageId, reason) => {
    try {
      if (onDeleteMessage) {
        await onDeleteMessage(messageId, reason);
      }
    } catch (error) {
      showError(
        'Failed to Delete Message',
        `Could not delete the message: ${error.message}. Please try again.`,
      );
    }
  };

  const handleReplyMessage = (message, senderName, content, msgtype, filename, thumbnailUrl, size) => {
    if (editingMessage) {
      cancelEdit();
      onSetMessage('');
    }
    startReply(message, senderName, content, msgtype, filename, thumbnailUrl, size);
  };

  const handleEditMessage = (message, content) => {
    if (replyingTo) {
      cancelReply();
    }
    startEdit(message, content);
  };

  const isAtRoomStart = useMemo(() => {
    if (!messages || messages.length === 0) {
      return false;
    }
    
    // Check if the first message group contains a room creation event
    const firstGroup = messages[0];
    const firstMessage = firstGroup?.messages?.[0];
    return firstMessage?.getType && firstMessage.getType() === 'm.room.create';
  }, [messages]);

  const renderMessageGroups = () => {
    const elements = [];
    let prevMessageTimeSeconds = null;

    messages.forEach((group) => {
      const firstMessage = group.messages?.[0];
      const timestampMs = firstMessage?.getTs ? firstMessage.getTs() : null;
      const timestampSeconds = typeof timestampMs === 'number' ? Math.floor(timestampMs / 1000) : null;

      const systemEventMsg = group.messages.find(
        msg => msg?.getType && TIMELINE_SYSTEM_EVENT_TYPES.includes(msg.getType()),
      );

      let needsSeparator = false;
      if (timestampSeconds) {
        const oneDayInMs = 1000 * 60 * 60 * 24;
        const curDate = new Date(timestampSeconds * 1000);
        const prevDate = prevMessageTimeSeconds ? new Date(prevMessageTimeSeconds * 1000) : null;

        if (prevDate && (
          curDate.getTime() - prevDate.getTime() >= oneDayInMs ||
          prevDate.getDate() !== curDate.getDate() ||
          prevDate.getMonth() !== curDate.getMonth() ||
          prevDate.getFullYear() !== curDate.getFullYear()
        )) {
          needsSeparator = true;
        }
        if (needsSeparator) {
          elements.push(
            <DateSeparator key={`separator-${group.key}`} unixTime={timestampSeconds} />,
          );
        }
        prevMessageTimeSeconds = timestampSeconds;
      }

      if (systemEventMsg) {
        elements.push(
          <SystemMessageSeparator
            key={`system-separator-${group.key}`}
            unixTime={timestampSeconds}
            msg={systemEventMsg}
          />,
        );
        return;
      }

      // --- Read Marker Splitting Logic ---
      const readMarkerIndex = showReadMarker && readMarkerId
        ? group.messages.findIndex(msg => msg.getId() === readMarkerId)
        : -1;
      if (readMarkerIndex > -1 && readMarkerIndex < group.messages.length - 1) {
        const readPart = group.messages.slice(0, readMarkerIndex + 1);
        const unreadPart = group.messages.slice(readMarkerIndex + 1);

        // Extract key from group to avoid React key prop spreading warning
        const { key: groupKey, ...groupPropsWithoutKey } = group;
        
        elements.push(
          <MessageGroup
            key={`${groupKey}-read`}
            {...groupPropsWithoutKey} // Pass all group props except 'key'
            messages={readPart}
            allMessageGroups={messages}
            onDeleteMessage={handleDeleteMessage}
            onReplyMessage={handleReplyMessage}
            onEditMessage={handleEditMessage}
            editHighlightedMessageId={editingMessage?.id}
            selectedMessageId={selectedMessageId}
            onReactionDetailsOpen={onReactionDetailsOpen}
            onMessageDetailsOpen={onMessageDetailsOpen}
            onFocusMessage={onFocusMessage}
            onContactClick={onContactClick}
            onEditHistoryOpen={onEditHistoryOpen}
          />,
        );

        if (showReadMarker) {
          elements.push(<UnreadMessageMarker key={`unread-marker-${groupKey}`} />);
        }

        elements.push(
          <MessageGroup
            key={`${groupKey}-unread`}
            {...groupPropsWithoutKey} // Pass all group props except 'key'
            messages={unreadPart}
            allMessageGroups={messages}
            onDeleteMessage={handleDeleteMessage}
            onReplyMessage={handleReplyMessage}
            onEditMessage={handleEditMessage}
            editHighlightedMessageId={editingMessage?.id}
            selectedMessageId={selectedMessageId}
            onReactionDetailsOpen={onReactionDetailsOpen}
            onMessageDetailsOpen={onMessageDetailsOpen}
            onFocusMessage={onFocusMessage}
            onContactClick={onContactClick}
            onEditHistoryOpen={onEditHistoryOpen}
          />,
        );
      } else {
        // Extract key from group to avoid React key prop spreading warning
        const { key: groupKey, ...groupPropsWithoutKey } = group;
        
        elements.push(
          <MessageGroup
            key={groupKey}
            {...groupPropsWithoutKey} // Pass all group props except 'key'
            allMessageGroups={messages}
            onDeleteMessage={handleDeleteMessage}
            onReplyMessage={handleReplyMessage}
            onEditMessage={handleEditMessage}
            editHighlightedMessageId={editingMessage?.id}
            selectedMessageId={selectedMessageId}
            onReactionDetailsOpen={onReactionDetailsOpen}
            onMessageDetailsOpen={onMessageDetailsOpen}
            onFocusMessage={onFocusMessage}
            onContactClick={onContactClick}
            onEditHistoryOpen={onEditHistoryOpen}
          />,
        );

        if (showReadMarker && readMarkerIndex === group.messages.length - 1) {
          const { key: groupKey } = group;
          elements.push(<UnreadMessageMarker key={`unread-marker-${groupKey}`} />);
        }
      }
    });

    return elements;
  };

  const handleSendMessage = useCallback(async () => {
    let content;
    let result;

    if (editingMessage) {
      content = getEditContent(value);
      result = await onSendMessage(content);
      if (result) {
        cancelEdit();
      }
    } else if (replyingTo) {
      content = getReplyContent(value);
      result = await onSendMessage(content);
      if (result) {
        cancelReply();
      }
    } else {
      // This is a standard text message
      content = { body: value, msgtype: 'm.text' };
      result = await onSendMessage(content);
    }

    if (result && result.event_id) {
      logger.debug('[ReadMarker] Setting read marker because user sent a message:', result.event_id);
      updateReadMarkerOnSend(result.event_id, result._roomId);
      // When the user sends a message, they have caught up.
      // Hide the "New Messages" marker for the rest of this session.
      setShowReadMarker(false);
    }

    return result;
  }, [
    editingMessage, value, replyingTo, onSendMessage, getEditContent, getReplyContent, cancelEdit, cancelReply, updateReadMarkerOnSend,
  ]);

    return (
        <div className="messages">
            <TimelinePagination
                isLoadingOlder={(isLoadingOlder || isFillingViewport) && !isAtRoomStart}
                isLoadingNewer={isLoadingNewer}
            />
            <div className="messages-list" ref={setMessagesListRef}>
                {renderMessageGroups()}
            </div>
            {
                (typingText) && (
                    <span className="messages-list-typing-indicator">
                        {typingText}
                    </span>
                )
            }
            <MessageInput
                ref={messageInputRef}
                roomId={roomId}
                disabled={disabled}
                message={value}
                placeholder={placeholder}
                replyingTo={replyingTo}
                editingMessage={editingMessage}
                onCancelReply={cancelReply}
                onCancelEdit={() => {
                    cancelEdit();
                    onSetMessage('');
                }}
                onSetMessage={onSetMessage}
                onSendMessage={handleSendMessage}
                onTriggerFileSelect={onTriggerFileSelect}
            />
        </div>
    );
});

Messages.displayName = 'Messages';

Messages.propTypes = {
  client: PropTypes.object,
  roomId: PropTypes.string, // Optional - can be null in NewChatView
  typingText: PropTypes.string,
  messages: PropTypes.array,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string, // For NewChatView when context is not available
  onSetMessage: PropTypes.func, // For NewChatView when context is not available
  onSendMessage: PropTypes.func,
  onDeleteMessage: PropTypes.func,
  onTriggerFileSelect: PropTypes.func,
  isLoadingOlder: PropTypes.bool,
  isLoadingNewer: PropTypes.bool,
  canPaginateBackward: PropTypes.bool,
  canPaginateForward: PropTypes.bool,
  windowPosition: PropTypes.oneOf(['live', 'historical', 'middle']),
  isFillingViewport: PropTypes.bool,
  onPaginateBackward: PropTypes.func,
  onPaginateForward: PropTypes.func,
  onReactionDetailsOpen: PropTypes.func,
  onMessageDetailsOpen: PropTypes.func,
  onFocusMessage: PropTypes.func,
  onContactClick: PropTypes.func,
  selectedMessageId: PropTypes.string,
  readMarkerId: PropTypes.string,
  setReadMarker: PropTypes.func,
  onEditHistoryOpen: PropTypes.func,
  unreadCount: PropTypes.number,
};

export default Messages;
